import { useParams, Link } from "react-router-dom"
import { useQuery, useMutation } from "urql"
import { RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { GlassCard } from "@/components/shared/glass-card"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { OutboundDLQEntryQuery, ReplayOutboundDLQEntryMutation } from "@/lib/graphql/outbound"
import type { ResultOf } from "@/lib/graphql/types"

type AttemptItem = NonNullable<ResultOf<typeof OutboundDLQEntryQuery>["outboundDlqEntry"]>["delivery"]["deliveryAttempts"][number]

const attemptColumns: Column<AttemptItem>[] = [
  {
    header: "#",
    cell: (row) => <span className="text-xs tabular-nums">{row.attemptNumber}</span>,
  },
  {
    header: "Status",
    cell: (row) => (
      <span className={`text-xs tabular-nums font-medium ${row.responseStatus >= 200 && row.responseStatus < 300 ? "text-green-600" : "text-red-500"}`}>
        {row.responseStatus > 0 ? row.responseStatus : "-"}
      </span>
    ),
  },
  {
    header: "Duration",
    cell: (row) => <span className="text-xs tabular-nums">{row.durationMs}ms</span>,
  },
  {
    header: "Error",
    cell: (row) => (
      <span className="text-xs text-muted-foreground max-w-60 truncate block">
        {row.error || "-"}
      </span>
    ),
  },
  {
    header: "Time",
    cell: (row) => <TimeAgo date={row.attemptedAt} />,
  },
]

export function OutboundDLQDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [result, reexecute] = useQuery({ query: OutboundDLQEntryQuery, variables: { id: id! } })
  const [, replayEntry] = useMutation(ReplayOutboundDLQEntryMutation)

  const entry = result.data?.outboundDlqEntry

  const handleReplay = async () => {
    if (!entry) return
    const res = await replayEntry({ id: entry.id })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("DLQ entry replayed")
    reexecute({ requestPolicy: "network-only" })
  }

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!entry) return <EmptyState title="DLQ entry not found" />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Outbound DLQ Entry</h1>
          {entry.replayedAt && (
            <StatusBadge status="DELIVERED" />
          )}
        </div>
        {!entry.replayedAt && (
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleReplay} className="shadow-primary-glow transition-shadow">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Replay
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Error</CardTitle>
          </CardHeader>
          <CardContent>
            <GlassCard className="p-3">
              <pre className="text-xs font-mono text-destructive whitespace-pre-wrap break-all">
                {entry.lastError}
              </pre>
            </GlassCard>
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{entry.id}</code>
                <CopyButton text={entry.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Status</FieldLabel>
              {entry.replayedAt ? (
                <div className="flex items-center gap-2">
                  <StatusBadge status="DELIVERED" />
                  <TimeAgo date={entry.replayedAt} />
                </div>
              ) : (
                <StatusBadge status="PENDING" />
              )}
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={entry.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Message</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">Event Type</FieldLabel>
              <Link to={`/messages/${entry.message.id}`} className="text-sm hover:underline">
                {entry.message.eventType}
              </Link>
            </div>
            <div>
              <FieldLabel className="mb-1">Application</FieldLabel>
              <Link to={`/applications/${entry.message.application.id}`} className="text-sm hover:underline">
                {entry.message.application.name}
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Delivery</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">Endpoint</FieldLabel>
              <Link to={`/endpoints/${entry.delivery.endpoint.id}`} className="text-xs font-mono hover:underline truncate block max-w-60">
                {entry.delivery.endpoint.url}
              </Link>
            </div>
            <GlassCard className="p-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-muted-foreground">Attempts</div>
                  <div className="font-medium tabular-nums">{entry.delivery.attempts}/{entry.delivery.maxAttempts}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Status</div>
                  <StatusBadge status={entry.delivery.status} />
                </div>
              </div>
            </GlassCard>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Delivery Attempts</h2>
        <DataTable
          columns={attemptColumns}
          data={entry.delivery.deliveryAttempts}
          keyFn={(row) => row.id}
          emptyTitle="No attempts recorded"
        />
      </div>
    </div>
  )
}
